/**
 * V2 PIN 오류 응답 → NetworkFetchError 변환
 *
 * V2 API는 PIN 오류를 아래 형식으로 반환한다.
 * { success: false, statusCode, error: { code, message, details: { remainPinTryCnt, ... } } }
 *
 * 화면들은 V1 시절부터 NetworkFetchError의 isInvalidPIN / remainPinTryCnt getter로
 * 분기하므로, 서비스 계층에서 이 형식으로 맞춰준다.
 */

import NetworkFetchError from '@/erros/NetworkFetchError'
import APIRequest from '@/networks/api/APIRequest'
import APIResponse from '@/networks/api/APIResponse'
import type { RequestMethod } from '@/networks/api/APITypes'

/**
 * PIN 계열로 취급할 V2 에러 코드
 *
 * WALLET_BLOCKED(423)도 포함한다. 잠김은 PIN 시도 소진의 결과이고,
 * 화면은 remainPinTryCnt === 0과 keyinfo의 isBlocked로 잠김을 판단한다.
 * 여기서 걸러내면 잠김 상태가 네트워크 오류 페이지로 빠진다.
 *
 * "PIN 소진의 결과"라는 전제는 백엔드에서 확인됐다. isBlocked를 true로 만드는
 * 경로는 decreasePinCount()가 잔여 0에 도달했을 때 호출하는 setWalletBlock()
 * 하나뿐이고, 관리자 차단 같은 다른 사유는 없다.
 */
const PIN_ERROR_CODES = ['PIN_INVALID', 'WALLET_BLOCKED'] as const

type PinErrorCode = (typeof PIN_ERROR_CODES)[number]

type V2ErrorBody = {
  error?: {
    code?: string
    message?: string
    details?: { remainPinTryCnt?: number }
  }
}

/**
 * NetworkFetchError가 읽는 V1 호환 오류 본문
 *
 * getter가 message.data.RemainPinTryCnt 한 경로만 보므로 그 구조를 그대로 만든다.
 *
 * 주의: 이 타입은 문서 역할만 한다. APIResponse의 data가 unknown이라
 * NetworkFetchError 쪽 FACADE_RESPONSE와 구조적으로 연결되지 않는다.
 * 그쪽에서 필드명을 바꾸면 컴파일은 통과하고 런타임에만 깨진다.
 */
type V1PinErrorData = {
  statusCode: number
  code: PinErrorCode
  message: {
    message: string
    data: { RemainPinTryCnt: number }
  }
}

const isPinErrorCode = (code: string): code is PinErrorCode =>
  PIN_ERROR_CODES.includes(code as PinErrorCode)

/**
 * 잔여 시도 횟수를 판별한다.
 *
 * WALLET_BLOCKED는 잔여 0이 확정된 상태다. 서버가 remainPinTryCnt를 빠뜨려도
 * 0으로 간주해야 한다. undefined를 반환하면 호출부가 기존 오류 처리로 넘겨
 * 잠김이 네트워크 오류 페이지로 빠지는데, 이 파일이 막으려는 시나리오와 같다.
 */
const resolveRemainPinTryCnt = (
  code: PinErrorCode,
  remainPinTryCnt: number | undefined,
): number | undefined => {
  if (typeof remainPinTryCnt === 'number') return remainPinTryCnt
  return code === 'WALLET_BLOCKED' ? 0 : undefined
}

/**
 * V2 오류 응답이 PIN 계열이면 NetworkFetchError로 변환해 반환한다.
 * PIN 계열이 아니거나 잔여 횟수를 판별할 수 없으면 undefined를 반환하므로,
 * 호출부는 기존 오류 처리를 그대로 이어가면 된다.
 *
 * @param body - 파싱된 응답 본문
 * @param response - fetch Response
 * @param method - 요청 메서드. APIResponse가 요구하는 request 구성에 사용한다
 */
export const toPinFetchError = (
  body: unknown,
  response: Response,
  method: RequestMethod,
): NetworkFetchError | undefined => {
  if (typeof body !== 'object' || body === null) return undefined

  const { error } = body as V2ErrorBody
  if (!error?.code || !isPinErrorCode(error.code)) return undefined

  const remainPinTryCnt = resolveRemainPinTryCnt(
    error.code,
    error.details?.remainPinTryCnt,
  )
  if (remainPinTryCnt === undefined) return undefined

  const message = error.message ?? 'Invalid PIN'
  const apiResponse = new APIResponse<undefined, V1PinErrorData, APIRequest>({
    data: {
      statusCode: response.status,
      code: error.code,
      message: {
        message,
        data: { RemainPinTryCnt: remainPinTryCnt },
      },
    },
    status: response.status,
    request: new APIRequest({ url: response.url, method }),
  })

  return new NetworkFetchError(apiResponse, message)
}
